﻿import { writable } from 'svelte/store';
import type { AsyncState } from './async-state';

export function createAsyncStore<TData, TArgs extends unknown[]>(
    loader: (...args: TArgs) => Promise<TData>
) {
    const initialState: AsyncState<TData> = {
        data: null,
        loading: false,
        error: null,
    };

    const { subscribe, set, update } = writable<AsyncState<TData>>(initialState);

    let lastArgs: TArgs | null = null;

    async function load(...args: TArgs): Promise<TData | null> {
        lastArgs = args;

        update((state) => ({
            ...state,
            loading: true,
            error: null,
        }));

        try {
            const data = await loader(...args);

            set({
                data,
                loading: false,
                error: null,
            });

            return data;
        } catch (err) {
            set({
                data: null,
                loading: false,
                error: err instanceof Error ? err.message : 'Something went wrong',
            });

            return null;
        }
    }

    return {
        subscribe,
        load,

        reload(): Promise<TData | null> {
            if (!lastArgs) {
                return Promise.resolve(null);
            }

            return load(...lastArgs);
        },

        clear() {
            lastArgs = null;
            set(initialState);
        },
    };
}